import React from 'react';
import {
    ArrayValidator,
    AutomagicFormiflyField,
    FormiflyForm,
    NumberValidator,
    ObjectValidator,
    StringValidator,
} from 'formifly';
import { Geocoder } from '@mapbox/search-js-react';
import styled from 'styled-components';
import CloseButton from "./components/CloseButton";
import MultiSelectField from "./components/MultiSelectField";

const SearchForm = (props) => {
    const [geocoderValue, setGeocoderValue] = React.useState('');
    const {workplaces, works, lands, institutions, mapboxToken} = props.settings;

    const accommodationOptions = [
        {value: 'yes', label: 'Unterkunft möglich'},
        {value: 'no', label: 'keine Unterkunft'},
        {value: 'help', label: 'bei Unterkunftssuche behilflich'},
    ];

    const ageOptions = [
        {value: 'under_27', label: 'bis 27 Jahre'},
        {value: 'over_27', label: 'über 27 Jahre'},
    ];

    const radiusOptions = [
        {value: '5000', label: '5km'},
        {value: '10000', label: '10km'},
        {value: '20000', label: '20km'},
        {value: '50000', label: '50km'},
        {value: '100000', label: '100km'},
    ];
    const handleSubmit = (values) => {
        props.setFilterParams({
            ...values,
            ...{
                lat: props.filterParams.lat,
                lon: props.filterParams.lon,
            },
        });
    };

    const handleGeocodeRetrieval = (evt) => {
        props.setFilterParams({...props.filterParams, ...{
            lat: evt.properties.coordinates.latitude,
            lon: evt.properties.coordinates.longitude,
        }});
    }

    const handleGeocodeClearing = () => {
        props.setFilterParams({...props.filterParams, ...{
            lat: null,
            lon: null,
        }});
    }

    const shape = new ObjectValidator({
        text: new StringValidator(),
        term_age: new ArrayValidator(new StringValidator()),
        term_workplace_category_id: new ArrayValidator(new NumberValidator()),
        term_institution_id: new ArrayValidator(new NumberValidator()),
        term_work_category_id: new ArrayValidator(new NumberValidator()),
        term_land_id: new ArrayValidator(new NumberValidator()),
        term_accommodation: new ArrayValidator(new StringValidator()),
        radius: new NumberValidator(),
    });

    const searchAndClose = () => {
        // scroll to top
        window.scrollTo(0, 0);

        // Hacky approach because ref is not supported
        const forms = document.getElementsByClassName('workplace-formifly-form');

        if (forms.length) {
            forms[0].dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
        props.setIsSearchFormVisible(false);
    }

    return (
        <SearchFormBox>
            <FormiflyForm onSubmit={handleSubmit}
                          shape={shape}
                          defaultValues={props.defaultFilterParams}
                          className={'workplace-formifly-form'}>
                <FormContainer>
                    <FormHeading><span>Ö</span>-Finder</FormHeading>
                    <FormDescription>
                        Hier findest du deine passende Stelle. Über 50 Träger bieten deutschlandweit das Freiwillige
                        Ökologische Jahr (FÖJ) und den Ökologischen Bundesfreiwilligendienst (ÖBFD) an. Auf der Karte
                        werden dir alle Einsatzstellen angezeigt. Du kannst sie nach Kategorien filtern und so die
                        passende Stelle für dich finden.
                    </FormDescription>
                    <SearchTypeHeader>Filter</SearchTypeHeader>
                    <MultiSelectField label="Alter"
                                      name="term_age"
                                      options={ageOptions} />
                    {workplaces.length > 0 &&
                        <MultiSelectField label="Einrichtung"
                                          name="term_workplace_category_id"
                                          options={workplaces} />
                    }
                    {works.length > 0 &&
                        <MultiSelectField label="Tätigkeit"
                                          name="term_work_category_id"
                                          options={works} />
                    }
                    <MultiSelectField label="(Bundes-) Land"
                                      name="term_land_id"
                                      options={lands} />
                    <MultiSelectField label="Träger"
                                      name="term_institution_id"
                                      options={institutions} />
                    <MultiSelectField label="Unterkunft"
                                      name="term_accommodation"
                                      options={accommodationOptions} />
                    <SearchTypeHeader>Volltext- und Umkreis-Suche</SearchTypeHeader>
                    <AutomagicFormiflyField label="Volltext-Suche" name="text" />
                    <GeocoderBox>
                        <Geocoder
                            accessToken={mapboxToken}
                            options={{
                                language: 'de',
                                country: 'DE,DK,AT',  // Because we have workplaces in DE, DK and AT
                            }}
                            placeholder={'Ort'}
                            onRetrieve={handleGeocodeRetrieval}
                            onClear={handleGeocodeClearing}
                            value={geocoderValue}
                            onChange={new_value => setGeocoderValue(new_value)}
                        />
                    </GeocoderBox>
                    <AutomagicFormiflyField label="Radius"
                                            name="radius"
                                            options={radiusOptions} />
                    <SubmitButton type="submit" onClick={() => window.scrollTo(0, 0)}>Suche</SubmitButton>
                </FormContainer>
            </FormiflyForm>
            <CloseButton onClick={searchAndClose} />
        </SearchFormBox>
    );
};

const SearchFormBox = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    z-index: 100000;  // absurd high because the site head is at 99999
    background-color: white;
    overflow-x: auto;
    bottom: 0;
    
    @media (min-width: 900px) {
        position: absolute;
        left: auto;
        bottom: auto;
        width: 430px;
        border-radius: 50px 0 0 50px;
        box-shadow: -10px 10px 10px 0 rgb(0, 0, 0, 0.2);
        overflow-x: unset;
    }
`;

const FormHeading = styled.h2`
    margin: 0 1rem 1rem 1rem;
    font-weight: normal;

    & > span {
        font-weight: bolder;
    }
`;

const FormDescription = styled.p`
    font-size: 1rem;
    line-height: 1.5rem;
`;

const FormContainer = styled.div`
    padding: 40px;
    display: flex;
    flex-wrap: wrap;
    
    @media (max-width: 899px) {
        max-width: 430px;
        margin: 0 auto;
    }

    & * {
        color: rgb(0 64 49);
    }

    & .formifly-field-container {
        margin-bottom: 0;
        margin-top: 0;
    }
    
    & .formifly-field-container, & .formifly-field-container > div, & .formifly-text-field-input, & .formifly-select-field-input {
        width: 100%;
    }

    & .formifly-field-container > div {
        margin-right: 0;
    }
    
    & .formifly-field-container label {
        margin-top: 0;
    }
    
    & .formifly-multi-select-container .formifly-multi-select-menu {
        max-height: 500px;
        overflow-y: auto;
    }
`;

const SearchTypeHeader = styled.h4`
    font-size: 1.2rem;
    margin: 10px 0 0 0;
`;

const GeocoderBox = styled.div`
    & div[class$="Geocoder"] {
        box-shadow: none;
    }
    
    & input {
        box-shadow: none;
        border: 1px solid #003f31;
        border-radius: 5px;
    }
    
    width: 100%;
    padding: 1rem 0.5rem 0 0.5rem;
`;

const SubmitButton  = styled.button`
    width: 100%;
    margin: 1rem 1rem 1rem 0;
    padding: 0.5rem;
    background-color: whitesmoke;
    border-radius: 5px;
    border: 1px solid #003f31;
    text-transform: uppercase;

    &:hover, &:focus-visible {
        background-color: #003f31;
        color: whitesmoke;
        border-color: whitesmoke;
    }

    &:before {
        content: '→   ';
    }
`;

const ResetButton = styled.button`
    text-transform: uppercase;
`;


export default SearchForm;
